import React, { RefObject, SyntheticEvent, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import classNames from "classnames"

import Icon from "@/components/icon/Icon"
import Divider from "@/components/divider/Divider"
import TextButton from "@/components/button/TextButton"
import ImageFallback from "@/app/components/ImageFallback"
import { createAccountStore } from "@/stores/account"
import { useUnreadNotificationCountQuery } from "@/hooks/api/useNoticeQuery"
import { convertGender, convertGroupAge } from "@/utils/format"
import { getImagePath } from "@/utils/image"
import { Account } from "@/types/account"
import { Team } from "@/types/team"
import { CustomIconType, RemixIconType } from "@/types/components/icon"
import { AccountType } from "@/constants/account"

import "./appBar.scss"
import useRouter from "@/hooks/router/useRoute"
import { useAuth } from "@/providers/AuthProvider"

const MAX_TEAM_LOGO_SIZE = 40

export default function AppBar({
  isDrawerOpen,
  isMobile,
}: {
  isDrawerOpen: boolean
  isMobile?: boolean
}) {
  const { info, teamInfo, teamProfileInfo, setTeamProfileInfo, setSelectedIdfTeam } =
    createAccountStore()
  const router = useRouter()

  return (
    <div className={classNames("AppBarCont", { isDrawerOpen, isMobile })}>
      <div className="team-nav-btn-cont">
        {isMobile && (
          <div className="appbar-logo" onClick={() => router?.push("/home")}>
            <img src="/images/logo_horizontal.png" alt="Fitogether logo" width={145} height={22} />
          </div>
        )}
        <SelectTeamContainer
          team={teamProfileInfo}
          setTeam={setTeamProfileInfo}
          setSelectedIdfTeam={setSelectedIdfTeam}
          teams={teamInfo}
        />
      </div>
      <AccountContainer info={info} />
    </div>
  )
}

const handleClickOutside = (
  event: MouseEvent,
  refs: RefObject<HTMLDivElement | null>[],
  setOpen: (open: boolean) => void,
) => {
  const target = event.target as Node
  const isOutside = refs.every((ref) => ref?.current && !ref.current.contains(target))

  if (isOutside) {
    setOpen(false)
  }
}

function AppBarImage({
  image,
  iconType,
  size,
  alt,
  onClick,
}: {
  iconType: CustomIconType | RemixIconType
  size: number
  image?: string
  alt?: string
  onClick?: () => void
}) {
  const [isSquare, setIsSquare] = useState(false)
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 })

  const handleImageSize = (e: SyntheticEvent<HTMLImageElement, Event>) => {
    const { naturalWidth, naturalHeight } = e.currentTarget

    if (naturalWidth === naturalHeight) {
      // 정방형
      setIsSquare(true)
    } else {
      // 긴 방향 기준으로 100%
      setIsSquare(false)
    }

    setNaturalSize({ width: naturalWidth, height: naturalHeight })
  }
  const newWidth =
    naturalSize.height > MAX_TEAM_LOGO_SIZE && naturalSize.width > naturalSize.height
      ? (MAX_TEAM_LOGO_SIZE / naturalSize.height) * naturalSize.width
      : naturalSize.width
  const newHeight =
    naturalSize.height > MAX_TEAM_LOGO_SIZE ? MAX_TEAM_LOGO_SIZE : naturalSize.height

  if (image) {
    return (
      <img
        src={getImagePath(image)}
        alt={alt ?? "image"}
        onClick={onClick}
        onLoad={handleImageSize}
        className={classNames("appbar-image", { square: isSquare })}
        width={isSquare ? size : newWidth}
        height={isSquare ? size : newHeight}
      />
    )
  } else {
    return (
      <div className="IconCont" onClick={onClick}>
        <Icon type={iconType} width={size} height={size} />
      </div>
    )
  }
}

function SelectTeamContainer({
  team,
  setTeam,
  setSelectedIdfTeam,
  teams,
}: {
  setTeam: (team: Team) => void
  setSelectedIdfTeam: (selectedIdfTeam: number) => void
  team?: Team
  teams?: Team[]
}) {
  const [open, setOpen] = useState(false)
  const teamRef = useRef<HTMLDivElement>(null)
  const teamListRef = useRef<HTMLDivElement>(null)
  const router = useRouter()

  useEffect(() => {
    const handler = (e: MouseEvent) => handleClickOutside(e, [teamRef, teamListRef], setOpen)
    document.addEventListener("mousedown", handler)

    return () => {
      document.removeEventListener("mousedown", handler)
    }
  }, [teamRef, teamListRef])

  const onChangeTeam = (item: Team) => {
    setTeam(item)
    setOpen(false)
    setSelectedIdfTeam(item.idfTeam)
    router?.push("/home")
  }

  return (
    <div className="SelectTeamCont">
      <div className="appbar-team" onClick={() => setOpen(!open)} ref={teamRef}>
        <AppBarImage image={team?.image} iconType="no-team" size={40} alt="Team logo" />
        <div className="team-info">
          <p>{team?.name || "-"}</p>
          <p>
            {`${(team?.groupAge && convertGroupAge(team?.groupAge)) || "-"} / ${(team?.gender && convertGender(team?.gender)) || "-"}`}
          </p>
        </div>
        <Icon type={"arrow-down"} data-open={open} size={16} />
      </div>

      {open && (
        <div className="team-list" ref={teamListRef}>
          {teams?.map((item) => (
            <div key={item.idfTeam} className="team-item" onClick={() => onChangeTeam(item)}>
              <ImageFallback image={item.image} iconType="no-team" size={24} alt="Team logo" />
              <div className="team-info">
                <p>{item.name || "-"}</p>
                <p>
                  {convertGroupAge(item.groupAge) || "-"} / {convertGender(item.gender) || "-"}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function AccountContainer({ info }: { info?: Account }) {
  const { t } = useTranslation("")
  const { data: count } = useUnreadNotificationCountQuery()

  const router = useRouter()

  const [open, setOpen] = useState(false)
  const accountListRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = (e: MouseEvent) => handleClickOutside(e, [accountListRef], setOpen)
    document.addEventListener("mousedown", handler)

    return () => {
      document.removeEventListener("mousedown", handler)
    }
  }, [accountListRef])

  const { logout } = useAuth()
  const onClickLogout = () => logout?.()
  const toggleOpen = () => setOpen(!open)

  const navigationList = [{ text: t("button.myAccount"), link: "/account" }]

  return (
    <div className="AccountCont">
      <div className="account-noti" onClick={() => router?.push("/notice")}>
        <div className="account-noti-icon">
          <Icon type={count && !!count.count ? "noti-on" : "noti-off"} />
        </div>
      </div>
      <div className="account-wrapper">
        <div className="account-clickable" onClick={toggleOpen}>
          <AppBarImage
            image={info?.image}
            iconType="no-profile"
            size={40}
            alt="Your profile photo"
          />
          <div className="account-info">
            <p>{info?.name}</p>
            <p>{AccountType[info?.type as keyof typeof AccountType]}</p>
          </div>
        </div>

        {open && (
          <div className="account-list" ref={accountListRef}>
            <div className="account-list-info">
              <p>{info?.name}</p>
              <p>{info?.email}</p>
            </div>
            <Divider />
            <div className="account-navigation">
              {navigationList.map((item) => {
                return (
                  <div
                    key={item.text}
                    onClick={() => router?.push(item.link)}
                    className="account-menu-item">
                    <p>{item?.text}</p>
                    <div className="IconCont">
                      <Icon type="arrow-right" />
                    </div>
                  </div>
                )
              })}
            </div>
            <Divider />
            <div className="account-logout">
              <TextButton onClick={onClickLogout}>{t("button.logout")}</TextButton>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
